import {
  AlreadyPrrocessIcon,
  Block,
  Button,
  CanceledIcon,
  Column,
  ConfirmModal,
  ConsolidateUnprocessedIcon,
  FlexContainer,
  FullscreenSheet,
  DataTableClient,
  DateCellComponent,
  NotFoundIcon,
  PackageCheckIcon,
  ScannerInput,
  SoundNotificationContext,
  Span,
  SuccessLineIcon,
  DefaultTableFilterProps,
  InputTableFilter,
  InputText,
  LanguageTypes,
  Modal,
  useNotifications,
  CopyToClipboard,
} from '@uni-space/uni-components';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { FocusEvent, KeyboardEvent, MouseEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { addParcelToBag, closeBag, getScalesWeight, printLabel } from '../api';
import { getUseScalesWeight } from '../model/scalesSettings';
import { useTranslation } from 'react-i18next';
import { SliderStateBags } from '../model/useBagsSettingsTable';
import { AddParcelToBagStatus } from '../model/manualSorting';
import { ParcelDto } from '@shared/types/parcel';
import { getApiError, getErrorMessageAsync } from '@shared/api';
import { ApiErrorCodes } from '@shared/types/api';

// Максимально допустимый вес брутто (кг), вводимый вручную при закрытии мешка.
const MAX_GROSS_WEIGHT_KG = 999;

export interface SlidingBagProps {
  isOpen: boolean;
  onClose: () => void;
  state: SliderStateBags;
  onRefetchTable?: () => Promise<void>;
  handleOpenDetails?: (bagId: number, bagNumber: string, sealAt: Date | null, bagCode: string) => Promise<void>;
  loadingDetails?: number[];
  isSortingTapeBags?: boolean;
}

type ChangeFilters<T> = {
  filterInputValues: InputTableFilter<T>[];
  id: string;
};

export default function SlidingBag(props: SlidingBagProps) {
  const {
    isOpen,
    onClose,
    state,
    onRefetchTable,
    handleOpenDetails,
    loadingDetails,
    isSortingTapeBags = false,
  } = props;
  const { addNotification } = useNotifications();
  const { onSoundSuccess, onSoundError, onSoundAlready } = useContext(SoundNotificationContext);
  const [isOpenAcceptance, setIsOpenAcceptance] = useState<boolean>(false);
  const [grossWeightInput, setGrossWeightInput] = useState<string>('');
  // Вес с весов (кг) для текущего закрытия мешка; null — весы не используются, ввод вручную.
  const [scalesWeightKg, setScalesWeightKg] = useState<number | null>(null);
  const [isFetchingScales, setIsFetchingScales] = useState<boolean>(false);
  // Текст расхождения веса: непустой — значит показываем модалку «перевзвесьте посылки».
  const [weightMismatchMessage, setWeightMismatchMessage] = useState<string | null>(null);
  const [isFetchLabel, setIsFetchLabel] = useState<boolean>(false);
  const { t, i18n } = useTranslation();
  const [currentBagAddedStatus, setCurrentBagAddedStatus] = useState<AddParcelToBagStatus | null>(null);
  const prevValue = useRef<string>('');
  const [filteredItems, setFilteredItems] = useState<ParcelDto[]>(state.bags.items);
  const [isFocusAnotherInput, setIsFocusAnotherInput] = useState<boolean>(false);

  const resetLastParcelStatus = useCallback(() => {
    prevValue.current = '';
    setCurrentBagAddedStatus(null);
  }, []);

  const handleClose = () => {
    onClose();
    resetLastParcelStatus();
  };

  const handleCloseAcceptance = () => {
    setIsOpenAcceptance(false);
    setGrossWeightInput('');
    setScalesWeightKg(null);
  };

  const columns: Column<ParcelDto>[] = useMemo(
    () => [
      {
        header: t('tableHeader.trackNumber'),
        accessor: 'trackNumber',
        renderCell: (row) => (
          <FlexContainer gap='6px' align='center'>
            <Span>{row.trackNumber}</Span>
            <CopyToClipboard
              text={row.trackNumber}
              resultLabel={t('copied')}
              placement="top-right"
            />
          </FlexContainer>
        ),
      },
      {
        header: t('tableHeader.external'),
        accessor: 'externalId',
      },
      {
        header: t('tableHeader.weight'),
        accessor: 'actualWeight',
        renderCell: (row) => (row.actualWeight && row.actualWeight.value ? row.actualWeight.value : row.weight.value),
      },
      {
        header: t('tableHeader.addedToBagAt'),
        accessor: 'addedToBagAt',
        renderCell: (row) => <DateCellComponent date={row.addedToBagAt} zone="local" />,
      },
    ],
    [state, t],
  );

  const filters: DefaultTableFilterProps<ParcelDto>[] = useMemo(
    () => [
      {
        type: 'search',
        id: 'trackNumber',
        name: 'trackNumber',
        label: t('tableHeader.trackNumber'),
        variant: 'primary',
        extractFieldByKey: (row) => row.trackNumber,
      },
    ],
    [t],
  );

  type AddParcelError = {
    responseStatus?: AddParcelToBagStatus;
    response?: { data?: { message?: string } };
  };

  const { mutateAsync: onSendBag, isPending } = useMutation({
    mutationKey: ['fetchAddParcelToBag'],

    mutationFn: async (trackNumber: string) => {
      prevValue.current = trackNumber;
      const res = await addParcelToBag(trackNumber, state.bagId);

      setCurrentBagAddedStatus(res.status);
      if (res.status === AddParcelToBagStatus.Added || res.status === AddParcelToBagStatus.AlreadyInBag) {
        return res;
      }

      throw {
        responseStatus: res.status,
      } satisfies AddParcelError;
    },

    onSuccess: async (res) => {
      await handleOpenDetails?.(state.bagId, state.bagNumber, state.closedAt, state.bagCode);
      // onRefetchTable?.();
      if (res.status === AddParcelToBagStatus.AlreadyInBag) {
        onSoundAlready();
        addNotification({ type: 'warning', message: handleStatus(res.status).label });
        return;
      }
      onSoundSuccess();
      addNotification({ type: 'success', message: handleStatus(res.status).label });
    },

    onError: (error: AddParcelError) => {
      if (error.responseStatus) {
        setCurrentBagAddedStatus(error.responseStatus);
      }

      addNotification({
        type: 'error',
        message: error.responseStatus
          ? handleStatus(error.responseStatus).label
          : (error.response?.data?.message ?? t('error')),
      });

      onSoundError();
    },
  });

  const handlePrintLabel = async () => {
    try {
      setIsFetchLabel(true);
      await printLabel(state.bagId);
    } catch (error) {
      addNotification({ type: 'error', message: t('unexpectedError') });
    } finally {
      setIsFetchLabel(false);
    }
  };

  const { mutateAsync: fetchCloseBag, isPending: isPendingCloseBag } = useMutation({
    mutationKey: ['fetchCloseBag'],
    mutationFn: ({ bagId, grossWeight }: { bagId: number; grossWeight?: number }) => {
      return closeBag(bagId, `Box ${bagId}`, grossWeight);
    },
    onSuccess: async () => {
      onSoundSuccess();
      await printLabel(state.bagId);
      onRefetchTable?.();
      setIsOpenAcceptance(false);
      setGrossWeightInput('');
      setScalesWeightKg(null);
      handleClose();
    },
    onError: async (error) => {
      onSoundError();

      // Расхождение веса — не обычная ошибка: закрываем ввод веса и показываем,
      // на сколько мешок разошёлся с расчётом, чтобы оператор перевесил посылки.
      const apiError = getApiError(error);
      if (apiError?.code === ApiErrorCodes.bagWeightMismatch) {
        setIsOpenAcceptance(false);
        setGrossWeightInput('');
        setScalesWeightKg(null);
        setWeightMismatchMessage(apiError.message ?? '');
        return;
      }

      addNotification({
        type: 'error',
        message: await getErrorMessageAsync(error, t('unexpectedError')),
      });
    },
  });

  // Вес брутто опционален: пустое поле — закрываем без веса.
  // Если значение введено, оно должно быть числом в диапазоне (0; MAX_GROSS_WEIGHT_KG].
  const trimmedGrossWeight = grossWeightInput.trim();
  const parsedGrossWeight = Number(trimmedGrossWeight);
  const isGrossWeightEmpty = trimmedGrossWeight === '';
  const isGrossWeightValid =
    Number.isFinite(parsedGrossWeight) && parsedGrossWeight > 0 && parsedGrossWeight <= MAX_GROSS_WEIGHT_KG;
  const grossWeightError =
    !isGrossWeightEmpty && !isGrossWeightValid ? t('grossWeightError', { max: MAX_GROSS_WEIGHT_KG }) : undefined;

  const handleSubmitCloseBag = () => {
    if (scalesWeightKg !== null) {
      fetchCloseBag({ bagId: state.bagId, grossWeight: scalesWeightKg });
      return;
    }
    if (!isGrossWeightEmpty && !isGrossWeightValid) {
      return;
    }
    const grossWeight = isGrossWeightEmpty ? undefined : parsedGrossWeight;
    fetchCloseBag({ bagId: state.bagId, grossWeight });
  };

  // Если настройка «брать вес с весов» включена — опрашиваем весы; при недоступных
  // весах или некорректном весе откатываемся на ручной ввод.
  const handleOpenAcceptance = async () => {
    if (!getUseScalesWeight()) {
      setScalesWeightKg(null);
      setIsOpenAcceptance(true);
      return;
    }

    setIsFetchingScales(true);
    try {
      const scales = await getScalesWeight();
      const weightKg = scales ? Number((scales.weight / 1000).toFixed(3)) : 0;

      if (weightKg > 0 && weightKg <= MAX_GROSS_WEIGHT_KG) {
        setScalesWeightKg(weightKg);
      } else {
        setScalesWeightKg(null);
        addNotification({ type: 'warning', message: t('scalesWeightUnavailable') });
      }
    } finally {
      setIsFetchingScales(false);
      setIsOpenAcceptance(true);
    }
  };

  const handleStatus = (status: AddParcelToBagStatus) => {
    switch (status) {
      case AddParcelToBagStatus.AlreadyInBag:
        return {
          label: t('alreadyScanned'),
          component: <AlreadyPrrocessIcon color={'#f57c00'} size="s" />,
        };
      case AddParcelToBagStatus.Added:
        return {
          label: t('added'),
          component: <SuccessLineIcon color={'green'} size="s" />,
        };
      case AddParcelToBagStatus.Canceled:
        return {
          label: t('canceled'),
          component: <CanceledIcon color="#EF6D7C" size="s" />,
        };
      case AddParcelToBagStatus.BagAlreadySealed:
        return {
          label: t('bagAlreadySealed'),
          component: <ConsolidateUnprocessedIcon color="#EF6D7C" size="s" />,
        };
      case AddParcelToBagStatus.NotFound:
        return {
          label: t('parcelNotFound'),
          component: <NotFoundIcon color="#EF6D7C" size="s" />,
        };
      case AddParcelToBagStatus.NotScanned:
        return {
          label: t('notScanned'),
          component: <NotFoundIcon color="#EF6D7C" size="s" />,
        };
      case AddParcelToBagStatus.QualityControl:
        return {
          label: t('qualityControl'),
          component: <PackageCheckIcon color="#EF6D7C" size="s" />,
        };
      case AddParcelToBagStatus.PostalParcelWasNotLabeled:
        return {
          label: t('postalParcelWasNotLabeled'),
          component: <NotFoundIcon color="#EF6D7C" size="s" />,
        };
      case AddParcelToBagStatus.UnMeetBagCode:
        return {
          label: t('unMeetBagCode'),
          component: <NotFoundIcon color="#EF6D7C" size="s" />,
        };
      case AddParcelToBagStatus.ReturnBagRequiresCanceledParcel:
        return {
          label: t('returnBagRequiresCanceledParcel'),
          component: <CanceledIcon color="#EF6D7C" size="s" />,
        };
      case AddParcelToBagStatus.BagWeightLimitReached:
        return {
          label: t('bagWeightLimitReached'),
          component: <ConsolidateUnprocessedIcon color="#EF6D7C" size="s" />,
        };
      default:
        return {
          label: t('parcelNotFound'),
          component: <NotFoundIcon color="#EF6D7C" size="s" />,
        };
    }
  };

  useEffect(() => {
    setFilteredItems(state.bags.items);
  }, [state.bags.items]);

  useEffect(() => {
    if (!isOpen) {
      resetLastParcelStatus();
    }
  }, [isOpen, resetLastParcelStatus]);


  // Поиск по треку внутри мешка сканируют сканером: текст в поле держим выделенным,
  // чтобы новый скан заменял старый номер, а не дописывался к нему.
  const selectSearchOnMouseUp = useRef(false);

  const getSearchInput = (target: EventTarget) =>
    target instanceof HTMLInputElement && target.closest('.table__filter-item') ? target : null;

  const handleSearchFocus = (event: FocusEvent<HTMLDivElement>) => {
    const input = getSearchInput(event.target);
    if (!input) return;
    input.select();
    selectSearchOnMouseUp.current = true;
  };

  // Клик по полю после фокуса снимает выделение — гасим его один раз
  const handleSearchMouseUp = (event: MouseEvent<HTMLDivElement>) => {
    if (!selectSearchOnMouseUp.current || !getSearchInput(event.target)) return;
    selectSearchOnMouseUp.current = false;
    event.preventDefault();
  };

  // Enter от сканера запускает поиск — после него выделяем номер под следующий скан
  const handleSearchKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const input = getSearchInput(event.target);
    if (!input || event.key !== 'Enter') return;
    requestAnimationFrame(() => input.select());
  };

  const handleFilterChange = (change: ChangeFilters<ParcelDto>) => {
    const { filterInputValues } = change;
    let nextItems = state.bags.items;

    filterInputValues.forEach((filter) => {
      if (filter.inputType !== 'search') {
        return;
      }

      const searchValue = String(filter.value ?? '').toLowerCase().trim();
      if (!searchValue) {
        return;
      }

      nextItems = nextItems.filter((row) => {
        const rawValue = filter.extractFieldByKey
          ? filter.extractFieldByKey(row)
          : row[filter.id as keyof ParcelDto];
        return String(rawValue ?? '').toLowerCase().includes(searchValue);
      });
    });

    setFilteredItems(nextItems);
  };

  return (
    <FullscreenSheet height={'100dvh'} open={isOpen} onClose={handleClose}>
      <ConfirmModal
        onClose={handleCloseAcceptance}
        isOpen={isOpenAcceptance}
        isLoading={isPendingCloseBag}
        onSubmit={handleSubmitCloseBag}
        disabled={scalesWeightKg === null && !isGrossWeightEmpty && !isGrossWeightValid}
        maxWidth="440px"
        desc={
          <FlexContainer direction="column" gap="12px">
            <Span>{t('closeBagConfirm')}</Span>
            {scalesWeightKg !== null ? (
              <Span fw={600}>{t('grossWeightFromScales', { weight: scalesWeightKg })}</Span>
            ) : (
              <InputText
                id="grossWeight"
                type="number"
                label={t('grossWeightLabel')}
                value={grossWeightInput}
                onChange={(val) => setGrossWeightInput(val)}
                error={grossWeightError}
              />
            )}
          </FlexContainer>
        }
        lang={i18n.language}
      />
      <Modal isOpen={weightMismatchMessage !== null} onClose={() => setWeightMismatchMessage(null)}>
        <FlexContainer direction="column" gap="16px" style={{ maxWidth: '440px' }}>
          <Block textAlign="center">
            <Span fs={24}>{t('bagWeightMismatchTitle')}</Span>
          </Block>
          <Span>{t('bagWeightMismatchDesc')}</Span>
          {weightMismatchMessage && <Span fs={15}>{weightMismatchMessage}</Span>}
          <Button onClick={() => setWeightMismatchMessage(null)}>{t('understood')}</Button>
        </FlexContainer>
      </Modal>
      <FlexContainer align="center" justify="space-between">
        <Block>
          <FlexContainer direction="column">
            <Span fs={24}>{state.bagNumber}</Span>
            <Span fs={15}>
              {t('bagCode')}: {state.bagCode}
            </Span>
          </FlexContainer>
        </Block>
        <FlexContainer gap="24px">
          <Button loading={isFetchLabel} onClick={handlePrintLabel} disabled={!state.closedAt} width="160px">
            {t('printLabel')}
          </Button>
          <Button onClick={handleClose} width="100px">
            {t('tableHeader.rollUp')}
          </Button>
        </FlexContainer>
      </FlexContainer>
      {!state.closedAt && !isSortingTapeBags && (
        <Block mV={16}>
          <ScannerInput
            buttonLabel={t('send')}
            lang={i18n.language}
            isFocusFilter={isFocusAnotherInput}
            isLoading={isPending || (loadingDetails !== undefined && loadingDetails?.length > 0)}
            label={t('inputNumber')}
            onSubmit={(trackNumber) => onSendBag(trackNumber)}
          />
        </Block>
      )}
      <Block mt={8} className="fancy-card">
        <FlexContainer style={{ width: '100%' }} justify="space-between" align="center">
          <Block>
            <FlexContainer direction="column" gap="6px">
              <Span>{t('currentWeight').replace('{{weight}}', String(state.scales.weight))}</Span>
              {state.scales.parcelsCount != null && (
                <Span>
                  {t('tableHeader.parcelsCount')}: {state.scales.parcelsCount}
                </Span>
              )}
              <Span>
                {currentBagAddedStatus !== null && (
                  <FlexContainer align="center" gap="6px">
                    {t('tableHeader.status')} {t('parcelNumber').toLowerCase()} {prevValue.current}:{' '}
                    {handleStatus(currentBagAddedStatus).label}
                    {handleStatus(currentBagAddedStatus).component}
                  </FlexContainer>
                )}
              </Span>
            </FlexContainer>
          </Block>
          {!isSortingTapeBags && (
            <Block>
              <Button
                size="s"
                disabled={Boolean(state.closedAt) || state.bags.items.length === 0}
                loading={isFetchingScales}
                onClick={handleOpenAcceptance}
                width="200px"
              >
                {t('closeBag')}
              </Button>
            </Block>
          )}
        </FlexContainer>
      </Block>
      <Block mt={16}>
        <div
          onFocusCapture={handleSearchFocus}
          onMouseUpCapture={handleSearchMouseUp}
          onKeyDownCapture={handleSearchKeyDown}
        >
          <DataTableClient<ParcelDto>
            enableColumnResizing={false}
            columns={columns}
            data={filteredItems}
            filters={filters}
            height="calc(100vh - 440px)"
            isLoading={false}
            onFocus={(isFocus: boolean) => setIsFocusAnotherInput(isFocus)}
            lang={i18n.language as LanguageTypes}
            onChange={handleFilterChange}
            onReset={() => setFilteredItems(state.bags.items)}
            onUpdate={() => setFilteredItems(state.bags.items)}
          />
        </div>
      </Block>
    </FullscreenSheet>
  );
}
